<template>
  <v-app v-if="token">
    <v-navigation-drawer
      v-model="drawer"
      app
      temporary
    >
      <v-list>
        <v-list-item @click="goCollaboratorsDashboard()">
          <v-list-item-icon>
            <v-icon>mdi-account</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>{{ getLabel("collaborators") }}</v-list-item-title>
          </v-list-item-content>
        </v-list-item>

        <v-list-item @click="goGroupsDashboard()">
          <v-list-item-icon>
            <v-icon>mdi-account-group</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>{{ getLabel("groups") }}</v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </v-list>
    </v-navigation-drawer>
    <v-app-bar color="primary" app dark flat>
      <v-app-bar-nav-icon v-if="!isSelectionMode" @click="drawer = !drawer" />

      <v-icon
        v-if="isSelectionMode"
        class="pr-3"
        @click="leaveSelectionMode"
      >
        mdi-arrow-left-circle
      </v-icon>

      <v-chip
        v-if="isSelectionMode"
        class="d-none d-sm-flex font-weight-bold"
        color="white"
        outlined
        label
      >
        {{ getLabel("selection_mode") }}
      </v-chip>

      <router-link v-if="!isSelectionMode" :to="{ name: 'dashboard'}">
        <v-btn link text class="mr-2" @click="cleanSearch">
          <v-img alt="UCPA" width="100px" :src="logo" />
        </v-btn>
      </router-link>

      <v-chip v-if="!isSelectionMode" class="d-none d-sm-flex font-weight-bold" color="white" outlined label>
        {{ getLabel("crm_collaborators") }}
      </v-chip>

      <v-spacer />
      <search-bar />
      <v-spacer />
      <v-btn v-if="!isSelectionMode && showAddGroup" large outlined class="font-weight-bold" @click="goNewGroup()">
        <v-icon left>mdi-account-multiple-plus</v-icon> {{ getLabel("add_group") }}
      </v-btn>
      <v-btn v-if="!isSelectionMode && !showAddGroup" large outlined class="font-weight-bold" @click="goNewCollaborator()">
        <v-icon left>mdi-account-plus</v-icon>{{ getLabel("add_collaborator") }}
      </v-btn>
      <UserInfo />
    </v-app-bar>

    <!-- Sizes your content based upon application components -->
    <v-main>
      <v-progress-linear v-if="loadingBar || loadingWorkspaces" indeterminate color="primary" />
      <!-- Provides the application the proper gutter -->
      <!-- If using vue-router  -->
      <keep-alive :include="include">
        <router-view v-if="!loadingWorkspaces" v-show="!loadingBar" :key="route.meta.componentName" />
      </keep-alive>
      <TheNotification />
    </v-main>
  </v-app>
  <v-app v-else>
    <v-progress-linear indeterminate color="primary" />
  </v-app>
</template>

<script>
import { ref, computed } from '@vue/composition-api';
import { useQuery } from '@vue/apollo-composable';
import { useRouter, useRoute } from 'vue2-helpers/vue-router';

import { getGraphqlSchema } from './graphql/index';
import { useMainStore } from './store/index';
import { getLabel } from '@/utils/index';
import { getToken } from '@/utils/auth';

import logo from './assets/ucpa.svg';

import SearchBar from './components/search/SearchBar.vue';
import TheNotification from './components/UI/TheNotification.vue';
import UserInfo from './components/UI/UserInfo.vue';

export default {
  components: {
    SearchBar,
    UserInfo,
    TheNotification
  },
  setup () {
    const router = useRouter();

    const route = useRoute();

    const store = useMainStore();

    const token = getToken();

    const { cleanSearch } = store;

    const drawer = ref(false);

    const loadingBar = computed(() => store.loading);

    const include = computed(() => {
      if (store.addCollaboratorMode) {
        return ['ManageGroup'];
      }
      if (store.addGroupMode) {
        return ['ManageProfile'];
      }
      return store.include;
    });

    const showAddGroup = computed(() => route.meta.search === 'group');

    const isSelectionMode = computed(() => store.addGroupMode || store.addCollaboratorMode);

    const goCollaboratorsDashboard = () => {
      router.push({ name: 'dashboard' }).catch(error => {
        if (error.name !== 'NavigationDuplicated') {
          throw error;
        }
      });
    };

    const goGroupsDashboard = () => {
      router.push({ name: 'groupsDashboard' }).catch(error => {
        if (error.name !== 'NavigationDuplicated') {
          throw error;
        }
      });
    };

    const goNewCollaborator = () => {
      router.push({ name: 'newCollaborator' }).catch(error => {
        if (error.name !== 'NavigationDuplicated') {
          throw error;
        }
      });
    };

    const goNewGroup = () => {
      router.push({ name: 'newGroup' }).catch(error => {
        if (error.name !== 'NavigationDuplicated') {
          throw error;
        }
      });
    };

    // Get Workspaces and content types
    const { onError, onResult } = useQuery(getGraphqlSchema('getRolesWorkspacesContentTypes'), { fetchPolicy: 'no-cache' });

    const loadingWorkspaces = ref(true);

    onResult(result => {
      if (result.loading) {
        loadingWorkspaces.value = true;
        return;
      }

      loadingWorkspaces.value = false;

      store.roles = [...result.data.roles];

      // Projects with workspaces
      store.workspacesProjects = [
        { key: 'alpha', label: 'Alpha' },
        { key: 'crm', label: 'CRM Clients' },
        { key: 'collaborator', label: 'CRM Employees' },
        { key: 'iresa_search', label: 'iResa Search' },
        { key: 'labalaguere', label: 'LaBalaguere' },
        { key: 'speedboat', label: 'Speedboat' },
        { key: 'talent', label: 'Talent' },
        { key: 'recruiter', label: 'Recruiter' },
        { key: 'kala', label: 'KALA' }
      ];

      store.workspaces = [...result.data.speedboatWorkspaces.items, ...result.data.alphaWorkspaces.items, ...result.data.crmWorkspaces.items, ...result.data.iresaWorkspaces.items, ...result.data.labalaguereWorkspaces.items, ...result.data.collaboratorWorkspaces.items, ...result.data.talentWorkspaces.items, ...result.data.recruiterWorkspaces.items,
      ...result.data.kalaWorkspaces.items];

      const speedboatContentTypes = Object.values(result.data.speedboatSchema.types).filter(obj => {
        if (obj.configs && obj.expose === true) {
          return obj;
        }
      }).map(obj => ({ name: obj.name, wtype: obj.configs.wtype, project: 'speedboat' }));

      const alphaContentTypes = Object.values(result.data.alphaSchema.types).filter(obj => {
        if (obj.configs && obj.expose === true) {
          return obj;
        }
      }).map(obj => ({ name: obj.name, wtype: obj.configs.wtype, project: 'alpha' }));

      store.contentTypes = [...speedboatContentTypes, ...alphaContentTypes];
    });

    onError(() => {
      loadingWorkspaces.value = false;

      router.push({ name: 'error' });
    });

    /* ************************************************************* */

    const leaveSelectionMode = () => {
      if (route.meta.search === 'group') {
        store.addCollaboratorMode = false;
        router.push({ name: 'manageProfile', params: { uuid: store.collaborator.uuid } });
        return;
      }
      store.addGroupMode = false;
      router.push({ name: 'manageGroup', params: { uuid: store.group.uuid } });
    };

    return {
      logo,
      getLabel,
      route,
      cleanSearch,
      drawer,
      token,
      loadingBar,
      include,
      showAddGroup,
      isSelectionMode,
      goCollaboratorsDashboard,
      goGroupsDashboard,
      goNewCollaborator,
      goNewGroup,
      loadingWorkspaces,
      leaveSelectionMode
    };
  }
};
</script>
